import type { LegalDocument, LoadCurrentDocument } from '@/shared/domain'
import { FlywaySelect, FormInput, PhoneField } from '@/shared/ui/components'
import { LegalCheckboxes } from '@/shared/ui/primitives'
import type { JoinFormValues } from './form-values'

export type PersonalInfoSectionProps = {
  values: JoinFormValues
  errors: Record<string, string>
  emailLocked: boolean
  phoneRequired: boolean
  phoneLocked: boolean
  canLinkExistingAccount: boolean
  linkedEmail: string | null
  onLinkExistingAccount: () => void
  isSubmitting: boolean
  tosAccepted: boolean
  privacyAccepted: boolean
  legalAlreadyAccepted: boolean
  onFieldChange: (field: keyof JoinFormValues, value: string) => void
  onTosChange: (value: boolean) => void
  onPrivacyChange: (value: boolean) => void
  onLoadCurrentDocument?: LoadCurrentDocument
  onDocumentsLoaded?: (documents: LegalDocument[]) => void
}

export function PersonalInfoSection({
  values,
  errors,
  emailLocked,
  phoneRequired,
  phoneLocked,
  canLinkExistingAccount,
  linkedEmail,
  onLinkExistingAccount,
  isSubmitting,
  tosAccepted,
  privacyAccepted,
  legalAlreadyAccepted,
  onFieldChange,
  onTosChange,
  onPrivacyChange,
  onLoadCurrentDocument,
  onDocumentsLoaded,
}: PersonalInfoSectionProps) {
  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FormInput
          id="firstName"
          label="First name"
          required
          value={values.firstName ?? ''}
          onChange={(v) => onFieldChange('firstName', v)}
          error={errors.firstName}
          disabled={isSubmitting}
        />
        <FormInput
          id="lastName"
          label="Last name"
          required
          value={values.lastName ?? ''}
          onChange={(v) => onFieldChange('lastName', v)}
          error={errors.lastName}
          disabled={isSubmitting}
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <FormInput
          id="email"
          type="email"
          label="Email"
          required
          value={values.email ?? ''}
          onChange={(v) => onFieldChange('email', v)}
          error={errors.email}
          disabled={isSubmitting || emailLocked}
        />
        {canLinkExistingAccount ? (
          <button
            type="button"
            onClick={onLinkExistingAccount}
            disabled={isSubmitting}
            className="self-start text-sm text-blaze underline underline-offset-2 hover:no-underline disabled:opacity-50"
          >
            Already have a BlindBook account? Sign in to link it
          </button>
        ) : null}
        {linkedEmail ? (
          <p className="text-sm text-fg-dim">Linked to your existing account, {linkedEmail}.</p>
        ) : null}
      </div>

      <PhoneField
        id="phone"
        label="Phone"
        required={phoneRequired}
        value={values.phone ?? ''}
        onChange={(v) => onFieldChange('phone', v)}
        error={errors.phone}
        disabled={isSubmitting || phoneLocked}
      />

      <FlywaySelect
        id="flywayRegion"
        required
        value={values.flywayRegion ?? ''}
        onValueChange={(v) => onFieldChange('flywayRegion', v)}
        error={errors.flywayRegion}
        disabled={isSubmitting}
      />

      {legalAlreadyAccepted ? null : (
        <>
          <LegalCheckboxes
            tosAccepted={tosAccepted}
            privacyAccepted={privacyAccepted}
            onTosChange={onTosChange}
            onPrivacyChange={onPrivacyChange}
            onLoadCurrentDocument={onLoadCurrentDocument}
            onDocumentsLoaded={onDocumentsLoaded}
          />
          {errors.legalDocuments ? (
            <p className="text-sm text-destructive">{errors.legalDocuments}</p>
          ) : null}
        </>
      )}
    </div>
  )
}
